import React, { useState, useMemo, useEffect } from 'react';
import { DollarSign, TrendingUp, AlertCircle, Plus, Trash2, CheckCircle2, ListChecks } from 'lucide-react';
import { api } from '../services/api';
import { useToast } from './Toast';

const addDays = (date, days) => {
    const d = typeof date === 'string' ? new Date(date + 'T00:00:00') : new Date(date);
    d.setDate(d.getDate() + days);
    return d;
};
const getDaysDiff = (start, end) => {
    const s = typeof start === 'string' ? new Date(start + 'T00:00:00') : new Date(start);
    const e = typeof end   === 'string' ? new Date(end   + 'T00:00:00') : new Date(end);
    s.setHours(0,0,0,0); e.setHours(0,0,0,0);
    return (e - s) / 86400000;
};
const formatDate = (d) => {
    const date = d instanceof Date ? d : new Date(d + 'T00:00:00');
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const FinancialsView = ({ tasks = [], projectData, onUpdateProject }) => {
    const { showToast } = useToast();
    const [workingCapital, setWorkingCapital] = useState(50000);
    const [interestRate, setInterestRate]     = useState(7.5);

    // Actual draws
    const [actualDraws,  setActualDraws]  = useState([]);
    const [drawForm,     setDrawForm]     = useState({ date: new Date().toISOString().split('T')[0], amount: '', notes: '' });
    const [isAddingDraw, setIsAddingDraw] = useState(false);
    const [loadingDraws, setLoadingDraws] = useState(false);

    useEffect(() => {
        if (projectData) {
            if (projectData.workingCapital) setWorkingCapital(projectData.workingCapital);
            if (projectData.interestRate)   setInterestRate(projectData.interestRate);
            if (Array.isArray(projectData.draws)) setActualDraws(projectData.draws);
        }
    }, [projectData]);

    const refreshDraws = async () => {
        if (!projectData?.id) return;
        setLoadingDraws(true);
        try { setActualDraws(await api.getDraws(projectData.id)); } catch(e) {}
        finally { setLoadingDraws(false); }
    };

    const handleAddDraw = async () => {
        const amt = parseFloat(drawForm.amount);
        if (!drawForm.date || !amt || amt <= 0) return showToast('Enter a date and a valid amount', 'error');
        if (!projectData?.id) return showToast('Save the project first', 'error');
        setIsAddingDraw(true);
        try {
            const saved = await api.addDraw(projectData.id, { date: drawForm.date, amount: amt, notes: drawForm.notes.trim() });
            setActualDraws(prev => [saved, ...prev]);
            setDrawForm({ date: new Date().toISOString().split('T')[0], amount: '', notes: '' });
            showToast('Draw logged');
        } catch(e) {
            showToast('Failed to log draw: ' + e.message, 'error');
        } finally {
            setIsAddingDraw(false);
        }
    };

    const handleDeleteDraw = async (id) => {
        if (!window.confirm('Delete this draw record?')) return;
        setActualDraws(prev => prev.filter(d => d.id !== id));
        try { await api.deleteDraw(id); }
        catch(e) { showToast('Delete failed', 'error'); refreshDraws(); }
    };

    const handleSave = () => {
        if (onUpdateProject) onUpdateProject({ workingCapital: parseFloat(workingCapital), interestRate: parseFloat(interestRate) });
    };

    // ── Budget summary: actual vs estimated ──────────────────────────────────
    const budgetSummary = useMemo(() => {
        const totalEstimated = tasks.reduce((s, t) => s + (parseFloat(t.budgetEstimated) || 0), 0);
        const totalActual    = tasks.reduce((s, t) => s + (parseFloat(t.budgetActual)    || 0), 0);
        const variance       = totalActual - totalEstimated;
        const pctSpent       = totalEstimated > 0 ? Math.round((totalActual / totalEstimated) * 100) : 0;
        const tasksWithCosts = tasks.filter(t => (parseFloat(t.budgetEstimated) || 0) > 0 || (parseFloat(t.budgetActual) || 0) > 0);
        return { totalEstimated, totalActual, variance, pctSpent, tasksWithCosts };
    }, [tasks]);

    const totalActualDrawn = useMemo(() => actualDraws.reduce((s, d) => s + (parseFloat(d.amount) || 0), 0), [actualDraws]);

    // ── Loan economics simulation ─────────────────────────────────────────────
    const economics = useMemo(() => {
        if (!tasks.length) return null;

        // Only tasks with a parseable start date can be simulated. A single bad
        // date would otherwise make totalDays NaN and throw on new Array(NaN).
        const validTasks = tasks.filter(t => {
            if (!t.startDate || typeof t.startDate !== 'string') return false;
            const d = new Date(t.startDate + 'T00:00:00');
            return !isNaN(d.getTime());
        });
        if (!validTasks.length) return null;

        const dates    = validTasks.map(t => new Date(t.startDate + 'T00:00:00').getTime());
        const start    = new Date(Math.min(...dates));
        const endDates = validTasks.map(t => addDays(t.startDate, parseInt(t.duration) || 1).getTime());
        const end      = new Date(Math.max(...endDates));

        let totalDays = getDaysDiff(start, end) + 1;
        if (!Number.isFinite(totalDays) || totalDays < 1) return null;
        totalDays = Math.min(Math.floor(totalDays), 3650); // cap at 10 years as a sanity bound

        const dailyCosts = new Array(totalDays).fill(0);
        validTasks.forEach(t => {
            const cost = (parseFloat(t.budgetActual) > 0 ? parseFloat(t.budgetActual) : parseFloat(t.budgetEstimated)) || 0;
            if (!cost) return;
            const duration   = parseInt(t.duration) || 1;
            const rawIndex   = getDaysDiff(start, t.startDate);
            if (!Number.isFinite(rawIndex)) return;
            const startIndex = Math.max(0, Math.floor(rawIndex));
            const endIndex   = startIndex + duration - 1;
            if (endIndex >= 0 && endIndex < totalDays) dailyCosts[endIndex] += cost;
            else if (startIndex < totalDays) dailyCosts[totalDays - 1] += cost;
        });

        let cumulativeCost = 0, currentCash = parseFloat(workingCapital), loanBalance = 0;
        let pendingInterest = 0, cumulativeInterestPaid = 0;
        const draws = [], curvePoints = [];
        let prevDate = start;

        for (let i = 0; i < totalDays; i++) {
            const currentDate = addDays(start, i);
            if (i > 0 && currentDate.getMonth() !== prevDate.getMonth()) {
                cumulativeInterestPaid += pendingInterest; pendingInterest = 0;
            }
            cumulativeCost += dailyCosts[i];
            currentCash    -= dailyCosts[i];
            while (currentCash <= 0) {
                const drawAmount = parseFloat(workingCapital);
                loanBalance += drawAmount; currentCash += drawAmount;
                const existing = draws.find(d => d.dayIndex === i);
                if (existing) { existing.amount += drawAmount; existing.totalLoan = loanBalance; }
                else draws.push({ dayIndex: i, date: currentDate, amount: drawAmount, totalLoan: loanBalance });
            }
            if (loanBalance > 0) pendingInterest += loanBalance * (parseFloat(interestRate) / 100 / 365);
            curvePoints.push({ x: i, y: cumulativeCost, interest: cumulativeInterestPaid, loan: loanBalance });
            prevDate = currentDate;
        }
        return { totalCost: cumulativeCost, totalInterest: cumulativeInterestPaid + pendingInterest, finalLoan: loanBalance, draws, curvePoints, startDate: start, endDate: end, totalDays };
    }, [tasks, workingCapital, interestRate]);

    // ── Chart ─────────────────────────────────────────────────────────────────
    const renderChart = () => {
        if (!economics) return <div className="text-gray-400 p-10 text-center">Add tasks with dates and costs to see projections.</div>;
        const width = 600, height = 240, padding = 40;
        const maxY  = Math.max(economics.totalCost, economics.totalInterest) * 1.1;
        const maxX  = economics.totalDays;
        const getX = i => padding + (i / maxX) * (width - padding * 2);
        const getY = v => height - padding - (v / maxY) * (height - padding * 2);

        const ticks = 5;
        let costPath = '', interestPath = '';
        economics.curvePoints.forEach((p, i) => {
            costPath     += i === 0 ? `M ${getX(i)} ${getY(p.y)}` : ` L ${getX(i)} ${getY(p.y)}`;
            interestPath += i === 0 ? `M ${getX(i)} ${getY(p.interest)}` : ` L ${getX(i)} ${getY(p.interest)}`;
        });

        return (
            <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-full">
                {[...Array(ticks)].map((_, i) => {
                    const y = getY((i / (ticks - 1)) * maxY);
                    return <line key={i} x1={padding} y1={y} x2={width - padding} y2={y} stroke="#f1f5f9" strokeWidth="1"/>;
                })}
                {[0, 0.25, 0.5, 0.75, 1].map((frac, i) => {
                    const val = frac * maxX;
                    return (
                        <g key={i}>
                            <line x1={getX(val)} y1={padding} x2={getX(val)} y2={height - padding} stroke="#f1f5f9" strokeWidth="1"/>
                            <text x={getX(val)} y={height - 5} textAnchor="middle" fontSize="10" fill="#94a3b8">
                                {formatDate(addDays(economics.startDate, Math.round(val)))}
                            </text>
                        </g>
                    );
                })}
                <path d={interestPath} fill="none" stroke="#16a34a" strokeWidth="2"/>
                <path d={costPath}     fill="none" stroke="#2563eb" strokeWidth="3"/>
                {economics.draws.map((draw, i) => (
                    <line key={i} x1={getX(draw.dayIndex)} y1={padding} x2={getX(draw.dayIndex)} y2={height - padding} stroke="#dc2626" strokeWidth="1.5" strokeDasharray="5 3"/>
                ))}
            </svg>
        );
    };


    const varColor   = budgetSummary.variance > 0 ? 'text-red-600'   : budgetSummary.variance < 0 ? 'text-green-600'  : 'text-gray-400';
    const varSubCol  = budgetSummary.variance > 0 ? 'text-red-400'   : budgetSummary.variance < 0 ? 'text-green-500'  : 'text-gray-400';
    const varLabel   = budgetSummary.variance > 0 ? 'Over budget'    : budgetSummary.variance < 0 ? 'Under budget'    : 'On budget';
    const varStr     = (budgetSummary.variance > 0 ? '+$' : budgetSummary.variance < 0 ? '-$' : '$') + Math.abs(Math.round(budgetSummary.variance)).toLocaleString();
    const barColor   = budgetSummary.pctSpent > 100 ? 'bg-red-500'   : budgetSummary.pctSpent > 80  ? 'bg-amber-500'  : 'bg-blue-500';
    const remaining  = budgetSummary.totalEstimated - budgetSummary.totalActual;

    return (
        <div className="space-y-6 pb-20">

            {/* ── Budget vs Actual ──────────────────────────────────────────── */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="grid grid-cols-3 divide-x divide-gray-100">
                    <div className="p-5">
                        <div className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-1">Total Estimated</div>
                        <div className="text-3xl font-bold text-gray-800">${budgetSummary.totalEstimated.toLocaleString()}</div>
                        <div className="text-xs text-gray-400 mt-1">Sum of all task estimates</div>
                    </div>
                    <div className="p-5">
                        <div className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-1">Total Actual Paid</div>
                        <div className="text-3xl font-bold text-blue-600">${budgetSummary.totalActual.toLocaleString()}</div>
                        <div className="text-xs text-gray-400 mt-1">Sum of all task actual costs</div>
                    </div>
                    <div className="p-5">
                        <div className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-1">Variance</div>
                        <div className={'text-3xl font-bold ' + varColor}>{varStr}</div>
                        <div className={'text-xs mt-1 font-medium ' + varSubCol}>{varLabel}</div>
                    </div>
                </div>

                {budgetSummary.totalEstimated > 0 && (
                    <div className="px-5 pb-4">
                        <div className="flex justify-between text-xs text-gray-400 mb-1">
                            <span>{budgetSummary.pctSpent}% of budget spent</span>
                            <span>${remaining.toLocaleString()} remaining</span>
                        </div>
                        <div className="bg-gray-100 h-2.5 rounded-full overflow-hidden">
                            <div className={'h-2.5 rounded-full transition-all ' + barColor} style={{ width: Math.min(budgetSummary.pctSpent, 100) + '%' }}/>
                        </div>
                    </div>
                )}

                {budgetSummary.tasksWithCosts.length > 0 && (
                    <div className="border-t border-gray-100">
                        <div className="px-5 py-3 bg-gray-50 grid grid-cols-12 gap-2 text-xs font-bold text-gray-400 uppercase tracking-wide">
                            <span className="col-span-4">Task</span>
                            <span className="col-span-3 text-right">Estimated</span>
                            <span className="col-span-3 text-right">Actual</span>
                            <span className="col-span-2 text-right">Variance</span>
                        </div>
                        <div className="max-h-56 overflow-y-auto divide-y divide-gray-50">
                            {budgetSummary.tasksWithCosts.map(t => {
                                const est  = parseFloat(t.budgetEstimated) || 0;
                                const act  = parseFloat(t.budgetActual)    || 0;
                                const v    = act - est;
                                const estS = est > 0 ? ('$' + est.toLocaleString()) : '—';
                                const actS = act > 0 ? ('$' + act.toLocaleString()) : '—';
                                const vS   = (est > 0 && act > 0) ? ((v >= 0 ? '+$' : '-$') + Math.abs(Math.round(v)).toLocaleString()) : '—';
                                const vC   = est > 0 && act > 0 ? (v > 0 ? 'text-red-500' : v < 0 ? 'text-green-600' : 'text-gray-400') : 'text-gray-300';
                                return (
                                    <div key={t.id} className="px-5 py-2.5 grid grid-cols-12 gap-2 items-center hover:bg-gray-50 text-sm">
                                        <div className="col-span-4 font-medium text-gray-700 truncate">{t.name}</div>
                                        <div className="col-span-3 text-right text-gray-500">{estS}</div>
                                        <div className="col-span-3 text-right font-medium text-blue-600">{actS}</div>
                                        <div className={'col-span-2 text-right font-bold text-xs ' + vC}>{vS}</div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>

            {/* ── Loan settings ────────────────────────────────────────────── */}
            <div className="grid grid-cols-2 gap-4">
                <div className="bg-yellow-50 p-4 rounded-xl border border-yellow-100">
                    <label className="text-xs font-bold text-yellow-800 uppercase mb-1 block">Working Capital (Draw Amount)</label>
                    <div className="flex items-center gap-2">
                        <span className="text-yellow-600 font-bold text-lg">$</span>
                        <input type="number" className="bg-transparent border-b-2 border-yellow-200 focus:border-yellow-500 outline-none text-xl font-bold w-full py-1 text-yellow-900"
                            value={workingCapital} onChange={e => setWorkingCapital(e.target.value)} onBlur={handleSave}/>
                    </div>
                </div>
                <div className="bg-white p-4 rounded-xl border border-gray-200">
                    <label className="text-xs font-bold text-gray-500 uppercase mb-1 block">Annual Interest Rate (%)</label>
                    <div className="flex items-center gap-2">
                        <input type="number" className="border-b-2 border-gray-200 focus:border-blue-500 outline-none text-xl font-bold w-20 py-1"
                            value={interestRate} onChange={e => setInterestRate(e.target.value)} onBlur={handleSave}/>
                        <span className="text-gray-400 font-bold">%</span>
                    </div>
                </div>
            </div>

            {/* ── Financial Projection chart ────────────────────────────────── */}
            {economics && (
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="font-bold text-gray-800 flex items-center gap-2"><TrendingUp className="text-blue-500"/> Financial Projection</h3>
                    <div className="flex gap-4 text-xs">
                        <div className="flex items-center gap-1"><div className="w-3 h-1 bg-blue-600"></div> Cost</div>
                        <div className="flex items-center gap-1"><div className="w-3 h-1 bg-green-600"></div> Interest Paid</div>
                        <div className="flex items-center gap-1"><div className="w-1 h-3 bg-red-600"></div> Bank Draw</div>
                    </div>
                </div>
                <div className="h-64 w-full">{renderChart()}</div>
            </div>
            )}

            {/* ── Loan economics + projected draw schedule ──────────────────── */}
            {economics && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-gradient-to-br from-slate-800 to-slate-900 text-white p-6 rounded-xl shadow-lg">
                    <h3 className="font-bold text-lg mb-4 flex items-center gap-2"><DollarSign className="text-yellow-400"/> Loan Economics</h3>
                    <div className="space-y-4">
                        <div className="flex justify-between items-center border-b border-slate-700 pb-2">
                            <span className="text-slate-400">Peak Loan Balance</span>
                            <span className="text-xl font-bold">${economics.finalLoan.toLocaleString(undefined, {maximumFractionDigits: 0})}</span>
                        </div>
                        <div className="flex justify-between items-center border-b border-slate-700 pb-2">
                            <span className="text-slate-400">Total Draws</span>
                            <span className="text-xl font-bold">{economics.draws.length}</span>
                        </div>
                        <div className="flex justify-between items-center pt-2">
                            <span className="text-yellow-400 font-bold">Total Interest Paid</span>
                            <span className="text-3xl font-bold text-yellow-400">${economics.totalInterest.toLocaleString(undefined, {maximumFractionDigits: 0})}</span>
                        </div>
                    </div>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 max-h-72 overflow-y-auto">
                    <h3 className="font-bold text-gray-800 mb-1 flex items-center gap-2"><AlertCircle className="text-red-600"/> Projected Draw Schedule</h3>
                    <p className="text-xs text-gray-400 mb-3">Based on task budgets and timeline — not yet drawn</p>
                    <table className="w-full text-sm">
                        <thead className="bg-gray-50 text-gray-500 text-left sticky top-0">
                            <tr><th className="p-2">Date</th><th className="p-2">Funds Drawn</th><th className="p-2">Loan Balance</th></tr>
                        </thead>
                        <tbody>
                            {economics.draws.length === 0 ? (
                                <tr><td colSpan="3" className="p-4 text-center text-green-600">Funded by initial capital.</td></tr>
                            ) : economics.draws.map((draw, i) => (
                                <tr key={i} className="border-b hover:bg-gray-50">
                                    <td className="p-2 font-medium">{formatDate(draw.date)}</td>
                                    <td className="p-2 text-green-600 font-bold">+${draw.amount.toLocaleString()}</td>
                                    <td className="p-2 text-red-600 font-bold">${draw.totalLoan.toLocaleString()}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
            )}

            {/* ── Actual Draw Tracker ───────────────────────────────────────── */}
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                <div className="flex justify-between items-center mb-1">
                    <h3 className="font-bold text-gray-800 flex items-center gap-2"><ListChecks className="text-emerald-600"/> Actual Draws</h3>
                    <div className="text-right">
                        <div className="text-xs text-gray-400 uppercase font-bold">Total Drawn to Date</div>
                        <div className="text-xl font-bold text-emerald-600">${totalActualDrawn.toLocaleString()}</div>
                    </div>
                </div>
                <p className="text-xs text-gray-400 mb-4">Log every real draw against the loan as it happens.</p>

                <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-3 mb-4">
                    <div className="flex flex-col md:flex-row gap-2 mb-2">
                        <input type="date" className="border rounded p-2 text-sm flex-shrink-0"
                            value={drawForm.date} onChange={e => setDrawForm({ ...drawForm, date: e.target.value })}/>
                        <div className="relative flex-1">
                            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                            <input type="number" className="w-full border rounded p-2 pl-6 text-sm" placeholder="Amount drawn"
                                value={drawForm.amount} onChange={e => setDrawForm({ ...drawForm, amount: e.target.value })}
                                onKeyDown={e => e.key === 'Enter' && handleAddDraw()}/>
                        </div>
                        <button onClick={handleAddDraw} disabled={isAddingDraw}
                            className="flex items-center justify-center gap-1 bg-emerald-600 text-white px-4 py-2 rounded font-bold text-sm hover:bg-emerald-700 disabled:opacity-50">
                            <Plus size={15}/>{isAddingDraw ? 'Logging...' : 'Log Draw'}
                        </button>
                    </div>
                    <input className="w-full border rounded p-2 text-sm" placeholder="Notes (e.g. Draw #3 — framing complete)"
                        value={drawForm.notes} onChange={e => setDrawForm({ ...drawForm, notes: e.target.value })}/>
                </div>

                {loadingDraws ? (
                    <p className="text-sm text-gray-400 text-center py-4">Loading...</p>
                ) : actualDraws.length === 0 ? (
                    <div className="text-center py-8 border-2 border-dashed border-gray-100 rounded-lg">
                        <CheckCircle2 size={28} className="text-gray-200 mx-auto mb-2"/>
                        <p className="text-gray-400 text-sm">No draws logged yet.</p>
                    </div>
                ) : (
                    <div className="space-y-2 max-h-72 overflow-y-auto">
                        {actualDraws.map(draw => (
                            <div key={draw.id} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg border hover:bg-gray-100">
                                <div className="flex items-center gap-3">
                                    <div className="bg-emerald-100 text-emerald-700 rounded-full w-9 h-9 flex items-center justify-center flex-shrink-0">
                                        <DollarSign size={16}/>
                                    </div>
                                    <div>
                                        <div className="font-bold text-gray-800">${parseFloat(draw.amount).toLocaleString()}</div>
                                        <div className="text-xs text-gray-500">{formatDate(draw.date)}{draw.notes ? ' — ' + draw.notes : ''}</div>
                                    </div>
                                </div>
                                <button onClick={() => handleDeleteDraw(draw.id)} className="text-gray-300 hover:text-red-500 flex-shrink-0">
                                    <Trash2 size={16}/>
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default FinancialsView;
